import {addDays, Day, format, isSameDay, startOfWeek} from 'date-fns';
import {useComponentsTheme} from 'platform/components';
import {
  Box,
  getSize,
  getValueByDevice,
  Grid,
  GridItem,
  Heading,
  Hide,
  Show,
  Text,
  useDevice,
  VStack,
} from 'platform/foundation';
import {convertStringLocaleToDateFns, DayOfWeek, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {always, isNil} from 'ramda';

import {RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {DAYS_IN_WEEK} from '../constants/daysInWeek';
import {MAX_DAY_WIDTH} from '../constants/maxDayWidth';
import {useActiveId} from '../hooks/useActiveId';
import {useDnd} from '../hooks/useDnd';
import {useIsDragging} from '../hooks/useIsDragging';
import {BigCalendarEvent} from '../types/BigCalendarEvent';
import {calculateTimePosition} from '../utils/calculateTimePosition';
import {getAllDayHeaderElementId} from '../utils/getAllDayHeaderElementId';
import {getEventsForDate} from '../utils/getEventsForDate';
import {getTimeSlotElementId} from '../utils/getTimeSlotElementId';
import {isAllDayHeaderEvent} from '../utils/isAllDayHeaderEvent';
import {layoutEventSegments} from '../utils/layoutEventSegments';
import {scrollToCalendarStart} from '../utils/scrollToCalendarStart';
import {AllDayEventsGrid} from './AllDayEventsGrid';
import {CurrentTimeIndicator} from './CurrentTimeIndicator';
import {DroppableCell} from './DroppableCell';
import {EventItem} from './EventItem';
import {TimeSlot} from './TimeSlot';

interface WeekViewProps extends RequiredTestIdProps {
  currentDate: Date;
  events: BigCalendarEvent[];
  isReadOnly?: boolean;
  startHourView?: number;
  onEventClick?: (event: BigCalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  enableHover?: boolean;
  onEventMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onEventMouseLeave?: (event: BigCalendarEvent) => void;
}

export function WeekView(props: WeekViewProps) {
  const theme = useComponentsTheme();
  const device = useDevice();

  const {language, localeConfig} = useLocale();
  const {activeDnDId} = useDnd();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const allDayHeaderRef = useRef<HTMLDivElement>(null);

  const isDragging = useIsDragging();
  const activeId = useActiveId();

  const weekStart = startOfWeek(props.currentDate, {
    weekStartsOn: match<DayOfWeek, Day>(localeConfig.calendar.firstDayOfTheWeek)
      .with(DayOfWeek.MONDAY, always(1))
      .with(DayOfWeek.TUESDAY, always(2))
      .with(DayOfWeek.WEDNESDAY, always(3))
      .with(DayOfWeek.THURSDAY, always(4))
      .with(DayOfWeek.FRIDAY, always(5))
      .with(DayOfWeek.SATURDAY, always(6))
      .with(DayOfWeek.SUNDAY, always(0))
      .otherwise(always(1)),
  });

  const weekDays = Array.from({length: DAYS_IN_WEEK}, (_, day) => addDays(weekStart, day));
  const timeSlots = Array.from({length: 24}, (_, slot) => slot);

  const draggedEvent = props.events.find(
    (event) => event.id === activeId || event.id === activeDnDId
  );

  // The dragged event is drawn by the drag overlay, so its own bar is hidden while it moves.
  const allDaySegments = layoutEventSegments(
    props.events.filter(isAllDayHeaderEvent),
    weekStart
  ).filter((segment) => !(isDragging && segment.event.id === activeId));
  const hasAllDayEvents = allDaySegments.length > 0;

  useOnMount(() => {
    scrollToCalendarStart(props['data-testid'], props.startHourView);
  });

  // Keep the sticky day / all-day headers horizontally aligned with the grid.
  // The listeners live on scrollContainer (a node this component owns), so they
  // are torn down with it on unmount — no explicit cleanup needed.
  useOnMount(() => {
    const scrollContainer = scrollContainerRef.current;
    const header = headerRef.current;
    const allDayHeader = allDayHeaderRef.current;

    if (isNil(scrollContainer) || isNil(header) || isNil(allDayHeader)) {
      return;
    }

    let isSyncing = false;
    let touchMoveFrame: number | null = null;

    const syncScroll = (scrollLeft: number) => {
      if (isSyncing) {
        return;
      }
      isSyncing = true;

      requestAnimationFrame(() => {
        header.scrollLeft = scrollLeft;
        allDayHeader.scrollLeft = scrollLeft;
        isSyncing = false;
      });
    };

    const handleScroll = () => syncScroll(scrollContainer.scrollLeft);

    const handleTouchMove = () => {
      if (touchMoveFrame) {
        cancelAnimationFrame(touchMoveFrame);
      }
      touchMoveFrame = requestAnimationFrame(() => {
        syncScroll(scrollContainer.scrollLeft);
      });
    };

    syncScroll(scrollContainer.scrollLeft);

    scrollContainer.addEventListener('scroll', handleScroll, {passive: true});
    scrollContainer.addEventListener('touchmove', handleTouchMove, {passive: true});
    scrollContainer.addEventListener('touchend', handleScroll, {passive: true});
  });

  return (
    <Box position="relative" width="100%" height="100%">
      <div
        ref={headerRef}
        className="sticky overflow-hidden bg-[var(--color-palettes-neutral-10-100)]"
        style={{
          top: getSize(getValueByDevice(device, 10, 15)),
          zIndex: theme.zIndices.BIG_CALENDAR_HEADER,
        }}
      >
        <Grid columns={20} spacing={0}>
          <GridItem span={[3, 1]}>
            <Box height={[12, 15]} borderBottom="1px solid" borderColor="general.separator" />
          </GridItem>

          <GridItem span={[17, 19]}>
            <Grid columns={7} spacing={0}>
              {weekDays.map((date, dateIndex) => (
                <Box
                  key={`header-${date.toISOString()}`}
                  height={[12, 15]}
                  minWidth={MAX_DAY_WIDTH}
                  maxWidth={getValueByDevice(device, MAX_DAY_WIDTH, undefined)}
                  borderBottom="1px solid"
                  borderLeft="1px solid"
                  borderColor="general.separator"
                  paddingHorizontal={2}
                >
                  <VStack height="100%" align="flex-start" justify="center">
                    <Show onMobile>
                      <Heading
                        data-testid={suffixTestId(`weekView-dayHeader-[${dateIndex}]`, props)}
                        size={4}
                        color={isSameDay(date, new Date()) ? 'warning' : undefined}
                      >
                        {format(date, 'E', {locale: convertStringLocaleToDateFns(language)})}
                      </Heading>
                    </Show>
                    <Hide onMobile>
                      <Heading
                        size={4}
                        data-testid={suffixTestId(`weekView-dayHeader-[${dateIndex}]`, props)}
                      >
                        {format(date, 'EEEE', {locale: convertStringLocaleToDateFns(language)})}
                      </Heading>
                    </Hide>
                    <Text
                      data-testid={suffixTestId(`weekView-dayHeader-[${dateIndex}]`, props)}
                      color={isSameDay(date, new Date()) ? 'warning' : 'tertiary'}
                      alternative={isSameDay(date, new Date())}
                    >
                      {format(date, 'd', {locale: convertStringLocaleToDateFns(language)})}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </Grid>
          </GridItem>
        </Grid>
      </div>

      <div
        ref={allDayHeaderRef}
        id={getAllDayHeaderElementId(props['data-testid'])}
        className="sticky overflow-hidden bg-[var(--color-palettes-neutral-10-100)]"
        style={{
          top: getSize(getValueByDevice(device, 22, 30)),
          zIndex: theme.zIndices.BIG_CALENDAR_STICKY_ALLDAY_SLOT,
        }}
      >
        <Grid columns={20} spacing={0}>
          <GridItem span={[3, 1]}>
            <div
              className="h-full"
              style={{
                borderBottom: hasAllDayEvents
                  ? `1px solid ${theme.colors.general.separator}`
                  : 'none',
              }}
            />
          </GridItem>
          <GridItem span={[17, 19]}>
            <Show when={hasAllDayEvents}>
              <AllDayEventsGrid
                data-testid={suffixTestId('weekView', props)}
                segments={allDaySegments}
                onEventClick={props.onEventClick}
                enableHover={props.enableHover}
                onEventMouseEnter={props.onEventMouseEnter}
                onEventMouseLeave={props.onEventMouseLeave}
              />
            </Show>
          </GridItem>
        </Grid>
      </div>

      <Box ref={scrollContainerRef} overflow={isDragging ? 'hidden' : 'auto'}>
        <Grid data-testid={props['data-testid']} columns={20} spacing={0}>
          <GridItem span={[3, 1]}>
            {timeSlots.map((hour) => (
              <TimeSlot
                data-testid={suffixTestId(`weekView-[${hour}]`, props)}
                key={hour}
                hour={hour}
                id={getTimeSlotElementId(props['data-testid'], hour)}
              />
            ))}
          </GridItem>

          <GridItem span={[17, 19]}>
            <Grid columns={7} spacing={0}>
              {weekDays.map((date, dateIndex) => (
                <Box
                  minWidth={MAX_DAY_WIDTH}
                  maxWidth={getValueByDevice(device, MAX_DAY_WIDTH, undefined)}
                  key={date.toISOString()}
                  position="relative"
                >
                  {timeSlots.map((hour) => (
                    <DroppableCell
                      key={hour}
                      data-testid={suffixTestId(`weekView-[${dateIndex}]-[${hour}]`, props)}
                      date={date}
                      hour={hour}
                      draggedEvent={draggedEvent}
                      onDateClick={props.onDateClick}
                    />
                  ))}
                  <div
                    className="pointer-events-none absolute top-0"
                    style={{right: '3px', left: '4px'}}
                  >
                    <Show when={isSameDay(date, new Date())}>
                      <CurrentTimeIndicator data-testid={suffixTestId('weekView', props)} />
                    </Show>

                    {getEventsForDate(props.events, date).regularEvents.map((event, index) => {
                      if (isDragging && event.id === activeId) {
                        return null;
                      }

                      return (
                        <EventItem
                          key={event.id}
                          data-testid={suffixTestId(`weekView-[${dateIndex}]-[${index}]`, props)}
                          isOnWeekView
                          isReadOnly={props.isReadOnly}
                          event={event}
                          top={calculateTimePosition(event.start)}
                          onClick={props.onEventClick}
                          enableHover={props.enableHover}
                          onMouseEnter={props.onEventMouseEnter}
                          onMouseLeave={props.onEventMouseLeave}
                        />
                      );
                    })}
                  </div>
                </Box>
              ))}
            </Grid>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
}
